import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MdEditor, config, type StaticTextDefaultValue } from 'md-editor-rt';
import 'md-editor-rt/lib/style.css';
import classNames from 'classnames';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { Toggle } from '@/shared/ui/Toggle/Toggle';
import { Modal } from '@/shared/ui/Modal/Modal';
import { Select } from '@/shared/ui/Select/Select';
import { alertOpen } from '@/entities/Alert';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { StatusBadge } from '@/shared/ui/StatusBadge/StatusBadge';
import { Portal } from '@/shared/ui/Portal/Portal';
import { Loader } from '@/shared/ui/Loader/Loader';
import RadarIcon from '@/shared/assets/icons/radar.svg';
import {
  useGetNewsDetailQuery,
  useGetNewsListQuery,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useUploadNewsImageMutation,
  NewsItem,
} from '@/entities/News';
import { categoryOptions, roleDetails, buildActivePinnedByRole } from '../model/newsConstants';
import s from './NewsPage.module.scss';

const RU_RU: StaticTextDefaultValue = {
  toolbarTips: {
    bold: 'Жирный',
    underline: 'Подчеркнутый',
    italic: 'Курсив',
    strikeThrough: 'Зачеркнутый',
    title: 'Заголовок',
    sub: 'Подстрочный',
    sup: 'Надстрочный',
    quote: 'Цитата',
    unorderedList: 'Маркированный список',
    orderedList: 'Нумерованный список',
    task: 'Список задач',
    codeRow: 'Код в строке',
    code: 'Блок кода',
    link: 'Ссылка',
    image: 'Изображение',
    table: 'Таблица',
    mermaid: 'Диаграмма Mermaid',
    katex: 'Формула KaTeX',
    revoke: 'Отменить',
    next: 'Повторить',
    save: 'Сохранить',
    prettier: 'Форматировать',
    pageFullscreen: 'На всю страницу',
    fullscreen: 'На весь экран',
    preview: 'Предпросмотр',
    previewOnly: 'Только предпросмотр',
    htmlPreview: 'Предпросмотр HTML',
    catalog: 'Оглавление',
    github: 'Исходный код',
  },
  titleItem: {
    h1: 'Заголовок 1 уровня',
    h2: 'Заголовок 2 уровня',
    h3: 'Заголовок 3 уровня',
    h4: 'Заголовок 4 уровня',
    h5: 'Заголовок 5 уровня',
    h6: 'Заголовок 6 уровня',
  },
  imgTitleItem: {
    link: 'Добавить ссылку на изображение',
    upload: 'Загрузить изображение',
    clip2upload: 'Обрезать и загрузить',
  },
  linkModalTips: {
    linkTitle: 'Добавить ссылку',
    imageTitle: 'Добавить изображение',
    descLabel: 'Описание:',
    descLabelPlaceHolder: 'Введите описание...',
    urlLabel: 'Ссылка:',
    urlLabelPlaceHolder: 'Введите ссылку...',
    buttonOK: 'ОК',
  },
  clipModalTips: {
    title: 'Обрезать изображение',
    buttonUpload: 'Загрузить',
  },
  copyCode: {
    text: 'Копировать',
    successTips: 'Скопировано!',
    failTips: 'Ошибка копирования!',
  },
  mermaid: {
    flow: 'Блок-схема',
    sequence: 'Диаграмма последовательности',
    gantt: 'Диаграмма Ганта',
    class: 'Диаграмма классов',
    state: 'Диаграмма состояний',
    pie: 'Круговая диаграмма',
    relationship: 'Диаграмма сущность-связь',
    journey: 'Карта пути пользователя',
  },
  katex: {
    inline: 'Встрочная формула',
    block: 'Блочная формула',
  },
  footer: {
    markdownTotal: 'Количество символов',
    scrollAuto: 'Синхронная прокрутка',
  },
};

config({
  editorConfig: {
    languageUserDefined: {
      'ru-RU': RU_RU,
    },
  },
});

interface NewsEditorProps {
  id?: number;
}

export const NewsEditor: React.FC<NewsEditorProps> = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEdit = Boolean(id);

  const { data: newsDetail, isLoading: isDetailLoading } = useGetNewsDetailQuery(id as number, {
    skip: !isEdit,
  });

  const { data: listData } = useGetNewsListQuery({
    category: '',
    status: 'published',
    role: '',
    limit: 100,
    offset: 0,
  });
  const allPublishedNews = useMemo(() => listData?.news || [], [listData]);
  const activePinnedByRole = useMemo(() => buildActivePinnedByRole(allPublishedNews), [allPublishedNews]);

  const [createNews, { isLoading: isCreating }] = useCreateNewsMutation();
  const [updateNews, { isLoading: isUpdating }] = useUpdateNewsMutation();
  const [uploadNewsImage] = useUploadNewsImageMutation();

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [body, setBody] = useState('');
  const [coverImagePath, setCoverImagePath] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [category, setCategory] = useState('updates');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [pinnedRoles, setPinnedRoles] = useState<string[]>([]);
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [publishedAt, setPublishedAt] = useState<string>('');

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [pendingPinRole, setPendingPinRole] = useState<{ role: string; label: string; title: string } | null>(null);

  const updatePopupCoords = useCallback(() => {
    if (!wrapperRef.current || !popupRef.current) return;
    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const scrollX = window.scrollX ?? document.documentElement.scrollLeft;
    const scrollY = window.scrollY ?? document.documentElement.scrollTop;
    const popupWidth = Math.max(triggerRect.width, 260);
    popupRef.current.style.setProperty('--popup-top', `${triggerRect.bottom + scrollY + 4}px`);
    popupRef.current.style.setProperty('--popup-left', `${triggerRect.left + scrollX}px`);
    popupRef.current.style.setProperty('--popup-width', `${popupWidth}px`);
  }, []);

  useEffect(() => {
    if (!roleDropdownOpen) return;
    updatePopupCoords();
    const timer = setTimeout(updatePopupCoords, 0);
    window.addEventListener('resize', updatePopupCoords);
    window.addEventListener('scroll', updatePopupCoords, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePopupCoords);
      window.removeEventListener('scroll', updatePopupCoords, true);
    };
  }, [roleDropdownOpen, updatePopupCoords]);

  useEffect(() => {
    if (!roleDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const path = e.composedPath();
      if (popupRef.current && path.includes(popupRef.current)) return;
      if (wrapperRef.current && path.includes(wrapperRef.current)) return;
      setRoleDropdownOpen(false);
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [roleDropdownOpen]);

  useEffect(() => {
    if (isEdit && newsDetail) {
      setTitle(newsDetail.title || '');
      setSubtitle(newsDetail.subtitle || '');
      setBody(newsDetail.body || '');
      setCoverImagePath(newsDetail.coverImagePath || null);
      setCoverImageUrl(newsDetail.coverImageUrl || null);
      setCategory(newsDetail.category || 'updates');
      setSelectedRoles(newsDetail.targetRoles || []);
      setPinnedRoles(newsDetail.pinnedRoles || []);
      setStatus(newsDetail.status || 'draft');
      if (newsDetail.publishedAt) {
        const d = new Date(newsDetail.publishedAt);
        const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setPublishedAt(iso);
      } else {
        setPublishedAt('');
      }
    }
  }, [isEdit, newsDetail]);

  const uploadCover = async (file: File) => {
    setIsUploadLoading(true);
    try {
      const res = await uploadNewsImage({ image: file, isCover: true }).unwrap();
      setCoverImagePath(res.path);
      setCoverImageUrl(res.url);
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || 'Не удалось загрузить изображение';
      dispatch(alertOpen({ content: { header: '', body: [msg], footer: '' }, isError: true }));
    } finally {
      setIsUploadLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadCover(e.target.files[0]);
    }
  };

  const handleCoverDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleCoverDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleCoverDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        if (file.type.startsWith('image/')) {
          uploadCover(file);
        } else {
          dispatch(
            alertOpen({
              content: { header: '', body: ['Пожалуйста, загрузите файл изображения'], footer: '' },
              isError: true,
            }),
          );
        }
      }
    },
    [dispatch],
  );

  const handleRemoveCover = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCoverImagePath(null);
    setCoverImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditorImageUpload = async (files: File[], callback: (urls: string[]) => void) => {
    try {
      const urls: string[] = [];
      for (const file of files) {
        const res = await uploadNewsImage({ image: file, isCover: false }).unwrap();
        urls.push(res.url);
      }
      callback(urls);
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || 'Не удалось загрузить изображение в редактор';
      dispatch(
        alertOpen({
          content: { header: '', body: [msg], footer: '' },
          isError: true,
        }),
      );
    }
  };

  const handleRoleToggle = (roleVal: string) => {
    if (selectedRoles.includes(roleVal)) {
      setSelectedRoles(selectedRoles.filter((r) => r !== roleVal));
      if (pinnedRoles.includes(roleVal)) {
        setPinnedRoles(pinnedRoles.filter((r) => r !== roleVal));
      }
    } else {
      setSelectedRoles([...selectedRoles, roleVal]);
    }
  };

  const handlePinToggle = (roleVal: string) => {
    if (pinnedRoles.includes(roleVal)) {
      setPinnedRoles(pinnedRoles.filter((r) => r !== roleVal));
    } else {
      if (!selectedRoles.includes(roleVal)) {
        setSelectedRoles([...selectedRoles, roleVal]);
      }
      const activeNewsForRole = activePinnedByRole[roleVal];
      if (activeNewsForRole && activeNewsForRole.id !== id) {
        const rDetails = roleDetails.find((d) => d.value === roleVal);
        setPendingPinRole({
          role: roleVal,
          label: rDetails ? rDetails.label : roleVal,
          title: activeNewsForRole.title,
        });
      } else {
        setPinnedRoles([...pinnedRoles, roleVal]);
      }
    }
  };

  const handleConfirmPinReplacement = () => {
    if (pendingPinRole) {
      setPinnedRoles((prev) => [...prev, pendingPinRole.role]);
      setPendingPinRole(null);
    }
  };

  const handleSave = async (overrideStatus?: 'draft' | 'published') => {
    if (!title.trim()) {
      dispatch(alertOpen({ content: { header: '', body: ['Укажите заголовок новости'], footer: '' }, isError: true }));
      return;
    }
    if (!body.trim()) {
      dispatch(alertOpen({ content: { header: '', body: ['Укажите текст новости'], footer: '' }, isError: true }));
      return;
    }
    if (!category) {
      dispatch(alertOpen({ content: { header: '', body: ['Выберите категорию'], footer: '' }, isError: true }));
      return;
    }
    if (selectedRoles.length === 0) {
      dispatch(
        alertOpen({ content: { header: '', body: ['Выберите хотя бы одну целевую роль'], footer: '' }, isError: true }),
      );
      return;
    }

    const targetStatus = overrideStatus || status;
    if (overrideStatus) {
      setStatus(overrideStatus);
    }

    let pubDate: string | null = null;
    if (publishedAt) {
      pubDate = new Date(publishedAt).toISOString();
    }

    const payload = {
      title,
      subtitle: subtitle || null,
      body,
      coverImagePath: coverImagePath || null,
      category,
      targetRoles: selectedRoles,
      pinnedRoles,
      status: targetStatus,
      publishedAt: pubDate,
    };

    try {
      if (isEdit && id) {
        await updateNews({ id, ...payload }).unwrap();
        dispatch(
          alertOpen({
            content: { header: '', body: ['Новость успешно обновлена'], footer: '' },
            isError: false,
          }),
        );
      } else {
        await createNews(payload).unwrap();
        dispatch(
          alertOpen({
            content: { header: '', body: ['Новость успешно создана'], footer: '' },
            isError: false,
          }),
        );
      }
      navigate('/news');
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || 'Не удалось сохранить новость';
      dispatch(alertOpen({ content: { header: '', body: [msg], footer: '' }, isError: true }));
    }
  };

  if (isEdit && isDetailLoading) {
    return (
      <div className={s.loadingBox}>
        <Loader className={s.loader} />
      </div>
    );
  }

  return (
    <div className={s.editorLayout}>
      <div className={s.editorMain}>
        <Input
          value={title}
          onChange={(e: any) => setTitle(typeof e === 'string' ? e : e.target.value)}
          placeholder="Заголовок новости"
          error=""
          className={s.largeInput}
        />
        <Input
          value={subtitle}
          onChange={(e: any) => setSubtitle(typeof e === 'string' ? e : e.target.value)}
          placeholder="Краткое описание/анонс"
          error=""
        />
        <div className={s.mdeContainer}>
          <MdEditor
            modelValue={body}
            onChange={setBody}
            language="ru-RU"
            onUploadImg={handleEditorImageUpload}
            toolbars={[
              'bold',
              'underline',
              'italic',
              'strikeThrough',
              '-',
              'title',
              'quote',
              'unorderedList',
              'orderedList',
              '-',
              'link',
              'image',
              'table',
              '-',
              'revoke',
              'next',
              '=',
              'pageFullscreen',
              'fullscreen',
              'preview',
              'catalog',
            ]}
          />
        </div>
      </div>

      <div className={s.editorSidebar}>
        <div className={s.sidebarBox}>
          <h3>Настройки новости</h3>

          <div className={s.sidebarRow}>
            <span>Статус</span>
            <div className={s.statusTabs}>
              <button
                type="button"
                className={classNames(s.statusTab, status === 'draft' && s.active)}
                onClick={() => setStatus('draft')}
              >
                Черновик
              </button>
              <button
                type="button"
                className={classNames(s.statusTab, status === 'published' && s.active)}
                onClick={() => setStatus('published')}
              >
                Публикация
              </button>
            </div>
          </div>

          <div className={s.sidebarRow}>
            <span>Категория</span>
            <div className={s.sidebarSelect}>
              <Select value={category} options={categoryOptions} onChange={setCategory} />
            </div>
          </div>

          <div className={s.sidebarRow}>
            <span>Видимость</span>
            <div className={s.roleDropdownWrapper} ref={wrapperRef}>
              <div className={s.visibilityTrigger} onClick={() => setRoleDropdownOpen(!roleDropdownOpen)} tabIndex={0}>
                <div className={s.selectedRolesBadges}>
                  {selectedRoles.map((roleVal) => {
                    const r = roleDetails.find((d) => d.value === roleVal);
                    const isPinned = pinnedRoles.includes(roleVal);
                    return (
                      <StatusBadge
                        key={roleVal}
                        tone={isPinned ? 'green' : 'gray'}
                        customIcon={isPinned ? <RadarIcon /> : null}
                      >
                        {r ? r.label : roleVal}
                      </StatusBadge>
                    );
                  })}
                  {selectedRoles.length === 0 && (
                    <StatusBadge tone="red" customIcon={null}>
                      Выберите роль
                    </StatusBadge>
                  )}
                </div>
              </div>

              {roleDropdownOpen && (
                <Portal>
                  <div ref={popupRef} className={s.portalPopup}>
                    {roleDetails.map((role) => {
                      const isRoleChecked = selectedRoles.includes(role.value);
                      const isRolePinned = pinnedRoles.includes(role.value);
                      return (
                        <div key={role.value} className={s.roleRow}>
                          <span className={s.roleLabel}>{role.label}</span>
                          <div className={s.roleToggles}>
                            <div className={s.toggleItem}>
                              <span>Включить</span>
                              <Toggle isActive={isRoleChecked} onToggle={() => handleRoleToggle(role.value)} size="s" />
                            </div>
                            <div className={s.toggleItem}>
                              <span>Закрепить</span>
                              <Toggle isActive={isRolePinned} onToggle={() => handlePinToggle(role.value)} size="s" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className={s.dropdownFooter}>
                      <Button onClick={() => setRoleDropdownOpen(false)} size="xs">
                        Готово
                      </Button>
                    </div>
                  </div>
                </Portal>
              )}
            </div>
          </div>

          <div className={s.sidebarRow}>
            <span>Дата публикации</span>
            <div>
              <DateTimeInput
                mode="datetime"
                value={publishedAt}
                onChange={(val) => setPublishedAt(val)}
                onClear={() => setPublishedAt('')}
                placeholder="Выберите дату"
              />
            </div>
          </div>

          <div className={s.sidebarRow}>
            <span>Обложка</span>
            <div>
              {coverImageUrl ? (
                <div className={s.coverPreview}>
                  <img src={coverImageUrl} alt="Cover" />
                  <button type="button" className={s.removeCoverBtn} onClick={handleRemoveCover} title="Удалить">
                    ✕
                  </button>
                </div>
              ) : (
                <div
                  className={classNames(s.coverUploadArea, isDragOver && s.dragOver)}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleCoverDragOver}
                  onDragLeave={handleCoverDragLeave}
                  onDrop={handleCoverDrop}
                >
                  <p>
                    {isUploadLoading ? 'Загрузка...' : 'Выберите или перетащите сюда изображение для обложки (16:9)'}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={s.hiddenInput}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={s.sidebarActions}>
            <Button onClick={() => handleSave()} disabled={isCreating || isUpdating}>
              {isCreating || isUpdating
                ? 'Сохранение...'
                : isEdit
                  ? 'Сохранить изменения'
                  : status === 'draft'
                    ? 'Сохранить'
                    : 'Опубликовать'}
            </Button>
            {status === 'draft' && (
              <Button
                onClick={() => handleSave('published')}
                variant="outline"
                color="green"
                disabled={isCreating || isUpdating}
              >
                Опубликовать сейчас
              </Button>
            )}
            <Button onClick={() => navigate('/news')} variant="outline" color="red-light">
              Отмена
            </Button>
          </div>
        </div>
      </div>

      {pendingPinRole && (
        <Modal open={!!pendingPinRole} onClose={() => setPendingPinRole(null)} title="Замена закрепленной новости">
          <div className={s.modalContent}>
            Для роли &quot;{pendingPinRole.label}&quot; уже закреплена новость &quot;{pendingPinRole.title}&quot;.
            <br />
            Желаете её заменить?
          </div>
          <div className={s.modalActions}>
            <Button onClick={() => setPendingPinRole(null)} variant="outline" color="red-light" width="auto">
              Отмена
            </Button>
            <Button onClick={handleConfirmPinReplacement} width="auto">
              Да, заменить
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
